import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

import { setAuthToken } from '../../../../api/authToken';
import { setSessionExpiredHandler } from '../../../../api/sessionExpiry';
import { AuthContext } from '../../core/AuthContext';
import { AUTH_MODES } from '../../core/authModes';
import { decodeToken, displayNameFromToken } from '../../core/decodeToken';

/*
 * Password-grant login for a backend in the fake-jwt profile; selected
 * at runtime via /api/auth-config, which also supplies `tokenUrl` —
 * nothing is configured in the env. That profile offers no discovery
 * and no redirect flow (the OIDC library cannot talk to it), and no
 * renewal — an expired session leads back to the login page.
 */

const SESSION_STORAGE_KEY = 'fakeJwtAuthSession';

const isSessionExpired = (session) => {
  const exp = decodeToken(session?.token)?.claims?.exp;
  return typeof exp !== 'number' || exp * 1000 <= Date.now();
};

export const FakeJwtAuthProvider = ({ tokenUrl, children }) => {
  const [session, setSession] = useState(() => {
    const storedSession = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (!storedSession) return null;

    const parsedSession = JSON.parse(storedSession);
    if (isSessionExpired(parsedSession)) {
      window.localStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }
    return parsedSession;
  });

  const token = session?.token;

  useEffect(() => {
    setAuthToken(token);
  }, [token]);

  /* a 401 comes from the issuer of this token — without a refresh
     flow the session is over */
  useEffect(() => {
    setSessionExpiredHandler(() => {
      setSession((currentSession) => {
        if (!currentSession) return currentSession;
        window.localStorage.removeItem(SESSION_STORAGE_KEY);
        return null;
      });
    });
    return () => setSessionExpiredHandler(null);
  }, []);

  const value = useMemo(
    () => ({
      authMode: AUTH_MODES.FAKE_JWT,
      user: session?.user,
      token: session?.token,
      isAuthenticated: Boolean(session),
      isLoading: false,
      login: async ({ username, password } = {}) => {
        const response = await axios.post(
          tokenUrl,
          new URLSearchParams({ grant_type: 'password', username, password })
        );

        const accessToken = response.data?.access_token;
        if (!accessToken) {
          throw new Error(`No access_token in the response of ${tokenUrl}.`);
        }

        /* the fake-jwt token carries no name claims, only the subject */
        const claims = decodeToken(accessToken)?.claims;
        const user = {
          sub: claims?.sub ?? username,
          name: displayNameFromToken(accessToken) ?? claims?.sub ?? username,
        };

        const nextSession = { user, token: accessToken };

        window.localStorage.setItem(
          SESSION_STORAGE_KEY,
          JSON.stringify(nextSession)
        );

        setSession(nextSession);

        return nextSession;
      },
      logout: () => {
        window.localStorage.removeItem(SESSION_STORAGE_KEY);
        setSession(null);
      },
      error: null,
    }),
    [session, tokenUrl]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
